import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { OrganizationPartnerItemFragment } from '@client/graphql/__generated__/types';
import { memo } from 'react';
import { useNavigate } from 'react-router';

interface PartnerCardProps {
  partner: OrganizationPartnerItemFragment;
}

interface StatusBadgeProps {
  status: 'draft' | 'active' | 'inactive';
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const colorMap = {
    draft: 'orange.500',
    active: 'green.500',
    inactive: 'blackAlpha.500',
  };

  const labelMap = {
    draft: 'Draft',
    active: 'Active',
    inactive: 'Inactive',
  };

  return (
    <Flex
      alignItems="center"
      bg="blackAlpha.100"
      borderRadius="full"
      gap={1}
      px={2.5}
      py={1.5}
    >
      <Box bg={colorMap[status]} borderRadius="full" height="8px" width="8px" />
      <Text color="indigo.900" fontSize="sm" fontWeight="medium">
        {labelMap[status]}
      </Text>
    </Flex>
  );
};

export const PartnerCard = memo(function PartnerCard({
  partner,
}: PartnerCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/organizations/notifications/partners/${partner.id}`);
  };

  return (
    <Flex
      _hover={{ bg: 'whiteAlpha.800' }}
      alignItems="center"
      bg="white"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="lg"
      cursor="pointer"
      direction="column"
      height="130px"
      justifyContent="space-between"
      minWidth="130px"
      position="relative"
      transition="background 0.2s"
      onClick={handleClick}
    >
      <Box
        alignItems="center"
        borderRadius="sm"
        display="flex"
        flexGrow={1}
        height="50px"
        justifyContent="center"
        overflow="hidden"
        px={3}
      >
        {partner.logo?.url ? (
          <Image
            alt={partner.name}
            maxHeight="40px"
            maxWidth="100px"
            objectFit="contain"
            src={partner.logo.url}
          />
        ) : (
          <Text
            alignItems="center"
            color="gray.800"
            display="flex"
            fontSize="sm"
            fontWeight="medium"
            minHeight="40px"
            width="100px"
          >
            {partner.name}
          </Text>
        )}
      </Box>
      <Box mb={2}>
        <StatusBadge
          status={
            !partner.isActive
              ? 'inactive'
              : partner.hasDrafts
                ? 'draft'
                : 'active'
          }
        />
      </Box>
    </Flex>
  );
});
